// La caja de escribir a Santi (propuesta §5.1, P1/P4): lo único que firma es «Enviar». En un contrato que te toca,
// enviar le pasa el turno a Santi (la oficina deriva lo que le toca); la pista bajo la caja lo dice antes de enviar y deja
// el caso raro a un clic: «Al enviar, le toca a Santi · No, sigo yo» ↔ «Al enviar, te sigue tocando a ti · Que le toque a
// Santi» (vuelve a lo de siempre tras cada envío). Si espera a Santi, o en el chat, no hay pista: escribir no cambia nada.
// Enter envía, Mayús+Enter salta de línea; el borrador se guarda por hilo en el navegador (borradores.ts). Un envío que
// falló sin saber si llegó (5xx, red) se reintenta con la MISMA clave: el centro no lo repite; un «no» del servidor
// (4xx) termina aquel envío y el siguiente es otro mensaje con otra clave.
// Entrega 2 (04-11, P2): el borrador del agente de Santi cae en esta caja (borradorAgente.ts): tono «tuyo», «Borrador de
// tu agente · <porqué>», «Descartar» y «Otra versión»; con la caja vacía, «Que lo prepare tu agente». Nunca pisa lo que
// escribió Pau («Tu agente tiene una respuesta preparada · Verla»). Enviar lo manda como mensaje de Pau, igual que
// cualquier otro: es Pau quien firma.
// 04-11 (ADR D-037): la caja de la oficina (Compositor) — un campo con filo sobre el gris de ventana, sin barra de borde a
// borde, esquinas de 22; «Enviar» en cápsula de oro (el único oro), lo demás en cápsulas fantasma en gris. Con el borrador
// del agente, el fondo cálido de lo tuyo y el filo de oro tenue.
import { Bot, Loader2, RotateCcw, SendHorizontal } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { t } from '../../../i18n';
import { ApiHttpError } from '../../../lib/api';
import { shouldSendOnEnter } from '../../../lib/composer-keys';
import { cn } from '../../../lib/utils';
import { MD_QUERY, useMediaQuery } from '../../../hooks/useMediaQuery';
import { guardarBorrador, useBorrador } from '../chat/borradores';
import { envioTermino, mensajeErrorPelotas } from '../errores';
import { Boton } from '../ui';
import { LIMITES, pelotasApi } from './api';
import { useBorradorEnCaja } from './borradorAgente';
import { Contador, ENLACE } from './piezas';
import { pedirHilo, usePelotasStore } from './store';
import type { BorradorDTO } from './tipos';

export const claveBorradorSanti = (hilo: number | 'general') => `santi:${hilo}`;

/** Si el agente no deja nada en este tiempo tras «Que lo prepare tu agente», el botón vuelve y se dice. */
export const ESPERA_PREPARAR_MS = 3 * 60_000;

/** El mensaje de un fallo: si no se sabe si llegó, el de la propuesta; si el servidor dijo que no, su motivo. */
export function errorDeEnvio(e: unknown): string {
  return envioTermino(e) ? mensajeErrorPelotas(e) : t('oficinaV2.pelotas.chat.noSePudo');
}

/** El servidor no aceptó los datos (400 DATOS_INVALIDOS): se dice qué, en palabras, si se sabe. */
export const datosInvalidos = (e: unknown): boolean => e instanceof ApiHttpError && e.code === 'DATOS_INVALIDOS';

const mensajeLargo = (texto: string): string | null => (texto.trim().length > LIMITES.texto ? t('oficinaV2.pelotas.chat.textoLargo', { max: LIMITES.texto }) : null);

const vacio = (s: string) => !s.trim();
const igual = (a: string, b: string) => a === b;

/** «Que lo prepare tu agente» / «Otra versión»: pide y espera a que llegue una versión nueva (o se cansa y lo dice). */
export function usePreparar(hilo: number | 'general', enActual: string | null) {
  const preparar = usePelotasStore((s) => s.preparar);
  const [preparando, setPreparando] = useState<{ antes: string | null } | null>(null);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    if (preparando && enActual && enActual !== preparando.antes) setPreparando(null);
  }, [preparando, enActual]);
  useEffect(() => {
    if (!preparando) return;
    const id = setTimeout(() => {
      setPreparando(null);
      setError(t('oficinaV2.pelotas.borrador.sinRespuesta'));
    }, ESPERA_PREPARAR_MS);
    return () => clearTimeout(id);
  }, [preparando]);
  const pedir = useCallback(async () => {
    if (preparando) return;
    setError(null);
    setPreparando({ antes: enActual });
    try {
      await preparar(hilo);
    } catch {
      setPreparando(null);
      setError(t('oficinaV2.pelotas.borrador.noSePudo'));
    }
  }, [preparando, enActual, preparar, hilo]);
  return { preparando: !!preparando, error, pedir, limpiarError: () => setError(null) };
}

export function CompositorSanti({
  hilo,
  conTurno,
  corta = false,
  borrador = null,
}: {
  hilo: number | 'general';
  /** Le toca a Pau en este contrato: enviar le pasa el turno a Santi (y sale la pista). */
  conTurno: boolean;
  /** El cajón del agente empuja la caja: la pista en corto («Le toca a Santi · No, sigo yo»). */
  corta?: boolean;
  /** El borrador del agente de Santi para esta caja (o null). */
  borrador?: BorradorDTO | null;
}) {
  const claveCaja = claveBorradorSanti(hilo);
  const [texto, setTexto] = useBorrador(claveCaja);
  const [seguirYo, setSeguirYo] = useState(false);
  const [enviando, setEnviando] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const clave = useRef<string | null>(null);
  const area = useRef<HTMLTextAreaElement>(null);
  const dedo = useMediaQuery('(pointer: coarse)');
  const anchoEscritorio = useMediaQuery(MD_QUERY);
  const enterSaltaLinea = dedo && !anchoEscritorio;
  const descartarBorrador = usePelotasStore((s) => s.descartarBorrador);
  const descartar = useCallback(() => descartarBorrador(hilo), [descartarBorrador, hilo]);

  const caja = useBorradorEnCaja<string>({
    clave: claveCaja,
    borrador: borrador ? { en: borrador.en, valor: borrador.texto, porque: borrador.porque } : null,
    actual: texto,
    poner: setTexto,
    vacio,
    igual,
    alVaciar: descartar,
  });
  const prep = usePreparar(hilo, borrador?.en ?? null);

  useEffect(() => {
    const el = area.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, 200)}px`;
  }, [texto]);

  async function enviar() {
    // Sin nada que mandar, Enviar solo devuelve el foco a la caja (nunca un error).
    if (!texto.trim()) {
      area.current?.focus();
      return;
    }
    if (enviando) return;
    // Un borrador de antes puede pasar del límite (la caja ya no deja escribir más): se dice antes de enviar.
    const largo = mensajeLargo(texto);
    if (largo) {
      setError(largo);
      area.current?.focus();
      return;
    }
    setEnviando(true);
    setError(null);
    clave.current ??= crypto.randomUUID();
    try {
      await pelotasApi.mensaje(hilo, { texto: texto.trim(), seguirYo: conTurno && seguirYo }, clave.current);
      clave.current = null;
      // Lo que se envió era (o venía de) el borrador del agente: esa versión ya no vuelve; el servidor la borra.
      caja.soltar();
      setTexto('');
      guardarBorrador(claveCaja, '');
      setSeguirYo(false);
      pedirHilo(hilo);
      void usePelotasStore.getState().cargarLista();
      area.current?.focus();
    } catch (e) {
      if (envioTermino(e)) clave.current = null;
      setError((datosInvalidos(e) && mensajeLargo(texto)) || errorDeEnvio(e));
    } finally {
      setEnviando(false);
    }
  }

  function alDescartar() {
    caja.soltar();
    setTexto('');
    guardarBorrador(claveCaja, '');
    descartar();
    area.current?.focus();
  }

  function otraVersion() {
    caja.esperarVersion();
    void prep.pedir();
  }

  const avisoError = error ?? prep.error;
  return (
    <footer className="shrink-0 bg-oficina-elevada px-6 pb-4 pt-2" data-testid="compositor-santi">
      {avisoError ? (
        <p role="alert" className="mb-2 px-1 text-o-sub text-oficina-error">
          {avisoError}
        </p>
      ) : null}
      {caja.hayOtro ? (
        <p className="mb-2 flex items-center gap-1.5 px-1 text-o-sub text-oficina-texto2" data-testid="borrador-hay-otro">
          <Bot className="h-4 w-4 shrink-0 text-oficina-meta-pau" aria-hidden />
          {t('oficinaV2.pelotas.borrador.hayUno')} ·
          <button type="button" onClick={caja.verla} className={ENLACE} data-testid="borrador-verla">
            {t('oficinaV2.pelotas.borrador.verla')}
          </button>
        </p>
      ) : caja.deshacer ? (
        <p className="mb-2 flex items-center gap-1.5 px-1 text-o-sub text-oficina-texto2" data-testid="borrador-puesto">
          {t('oficinaV2.pelotas.borrador.puesto')} ·
          <button type="button" onClick={caja.deshacer} className={ENLACE} data-testid="borrador-deshacer">
            {t('oficinaV2.pelotas.borrador.deshacer')}
          </button>
        </p>
      ) : null}
      <div
        className={cn(CAJA, caja.enBorrador ? CAJA_BORRADOR : CAJA_NORMAL)}
        data-testid="compositor-caja"
        data-borrador={caja.enBorrador ? 'true' : undefined}
      >
        {caja.enBorrador ? <CabeceraBorrador porque={caja.porque} alDescartar={alDescartar} cerrar={borrador?.tipo === 'cerrar'} /> : null}
        <textarea
          ref={area}
          rows={1}
          value={texto}
          onChange={(e) => setTexto(e.target.value)}
          onKeyDown={(e) => {
            if (!e.nativeEvent.isComposing && shouldSendOnEnter(e, { isCoarsePointer: enterSaltaLinea })) {
              e.preventDefault();
              void enviar();
            }
          }}
          maxLength={LIMITES.texto}
          placeholder={t('oficinaV2.pelotas.chat.escribe')}
          aria-label={t('oficinaV2.pelotas.chat.escribe')}
          className={cn('block max-h-[200px] min-h-[52px] w-full resize-none bg-transparent px-4 pb-1 text-o-cuerpo text-oficina-texto outline-none placeholder:text-oficina-texto3', caja.enBorrador ? 'pt-0.5' : 'pt-3.5')}
          data-testid="compositor-texto"
        />
        <div className="flex items-center gap-2 px-1.5 pb-1.5 pt-1">
          <BotonAgente
            icono={caja.enBorrador ? RotateCcw : Bot}
            texto={t(caja.enBorrador ? 'oficinaV2.pelotas.borrador.otraVersion' : 'oficinaV2.pelotas.borrador.preparar')}
            preparando={prep.preparando}
            alPulsar={caja.enBorrador ? otraVersion : () => void prep.pedir()}
          />
          <span className="flex-1 pl-1">
            <Contador n={texto.length} max={LIMITES.texto} />
          </span>
          {conTurno ? (
            <span className="mr-1 inline-flex min-w-0 items-center gap-1.5 whitespace-nowrap text-o-sub text-oficina-texto3" data-testid="compositor-pista">
              {t(seguirYo ? (corta ? 'oficinaV2.pelotas.chat.pistaSiguesCorta' : 'oficinaV2.pelotas.chat.pistaSigues') : corta ? 'oficinaV2.pelotas.chat.pistaLeTocaCorta' : 'oficinaV2.pelotas.chat.pistaLeToca')} ·
              <button type="button" onClick={() => setSeguirYo((v) => !v)} aria-pressed={seguirYo} className={ENLACE} data-testid="compositor-seguir-yo">
                {t(seguirYo ? 'oficinaV2.pelotas.chat.queLeToque' : 'oficinaV2.pelotas.chat.noSigoYo')}
              </button>
            </span>
          ) : null}
          <Boton variante="primario" aria-disabled={!texto.trim()} onClick={() => void enviar()} data-testid="compositor-enviar">
            {enviando ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden /> : <SendHorizontal className="h-4 w-4" strokeWidth={2.2} aria-hidden />}
            {t('oficinaV2.pelotas.chat.enviar')}
          </Boton>
        </div>
      </div>
    </footer>
  );
}

/** La caja de escribir (también la de «Pedir algo»): el campo con filo de la oficina (chat/Compositor). */
export const CAJA = 'rounded-o-xl border transition-colors duration-150 ease-o-apple';
export const CAJA_NORMAL = 'border-white/[0.12] bg-oficina-fondo/40 focus-within:border-white/20';
/** Con el borrador del agente: el tono de «lo tuyo, aún no lo ha visto Santi» (propuesta §5.2). */
export const CAJA_BORRADOR = 'border-oficina-acento/[0.22] bg-oficina-tuyo';

/** Un botón fantasma en gris (el oro de `fantasma` es para enlaces sueltos; aquí el oro es solo «Enviar»). */
export const FANTASMA_GRIS = 'text-oficina-texto2 hover:text-oficina-texto';

/**
 * La fila de arriba de una caja con borrador: «Borrador de tu agente · <porqué>» y «Descartar». `cerrar`: el agente lo
 * daría por hecho («Tu agente lo daría por hecho · …»): el texto es la nota que lleva «Hecho» (arriba) si Pau lo pulsa.
 */
export function CabeceraBorrador({ porque, alDescartar, cerrar = false }: { porque: string; alDescartar: () => void; cerrar?: boolean }) {
  return (
    <div className="flex h-11 items-center gap-2 pl-4 pr-1.5 pt-1 text-o-sub font-semibold text-oficina-meta-pau" data-testid="borrador-cabecera">
      <Bot className="h-4 w-4 shrink-0" aria-hidden />
      <span className="shrink-0">{t(cerrar ? 'oficinaV2.pelotas.borrador.etiquetaCerrar' : 'oficinaV2.pelotas.borrador.etiqueta')}</span>
      {porque ? (
        <span className="min-w-0 truncate font-normal text-oficina-texto2" data-testid="borrador-porque">
          · {porque}
        </span>
      ) : null}
      <span className="flex-1" />
      <Boton variante="fantasma" tamano="sm" onClick={alDescartar} className={FANTASMA_GRIS} data-testid="borrador-descartar">
        {t('oficinaV2.pelotas.borrador.descartar')}
      </Boton>
    </div>
  );
}

/** «Que lo prepare tu agente» / «Otra versión» (fantasma en gris, robot o girar); mientras espera, «Preparando…» (sin
 *  apagarse: es lo que dice que está trabajando). */
export function BotonAgente({ icono: Icono, texto, preparando, alPulsar }: { icono: typeof Bot; texto: string; preparando: boolean; alPulsar: () => void }) {
  return (
    <Boton
      variante="fantasma"
      onClick={alPulsar}
      disabled={preparando}
      aria-busy={preparando || undefined}
      className={cn(FANTASMA_GRIS, 'px-3.5 disabled:opacity-100')}
      data-testid="borrador-preparar"
    >
      {preparando ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden /> : <Icono className="h-4 w-4" aria-hidden />}
      {preparando ? t('oficinaV2.pelotas.borrador.preparando') : texto}
    </Boton>
  );
}
